import { useCallbackRef } from '@chakra-ui/react';
import { throttle } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';

/**
 * bind ResizeObserver
 * @example
 * const [buttonRect, setButtonRect] = useState()
 * useResizeObserver({
 *   dom: buttonRef.current,
 *   handler: (buttonElem) => {
 *     setButtonRect(buttonElem.getBoundingClientRect())
 *   }
 * })
 * @example
 * const [buttonRect, setButtonRect] = useState()
 * const [myButtonRef] = useResizeObserver({
 *   handler: (buttonElem) => {
 *     setButtonRect(buttonElem.getBoundingClientRect())
 *   }
 * })
 * return (
 *   <Button ref={myButtonRef}>...</Button>
 * )
 */
export function useResizeObserver({
  dom,
  cssSelector,
  delay = 120,
  handler,
}: {
  dom?: HTMLElement | null;
  cssSelector?: string;
  delay?: number;
  handler: (dom: HTMLElement) => void;
}) {
  const [elem, setElemRef] = useState<HTMLElement | null>(null);
  const domElement = useMemo(() => {
    return typeof dom !== 'undefined'
      ? dom
      : typeof cssSelector !== 'undefined'
      ? (document.querySelector(cssSelector) as HTMLElement)
      : elem;
  }, [cssSelector, dom, elem]);
  const handlerRef = useCallbackRef(handler);

  useEffect(() => {
    if (!domElement) {
      return;
    }

    const throttleHandler = throttle(() => {
      handlerRef(domElement);
    }, delay);

    if (window.ResizeObserver) {
      const resizeObserver = new ResizeObserver(throttleHandler);
      resizeObserver.observe(domElement);
      return () => {
        resizeObserver.disconnect();
      };
    }

    // fallback to onResize
    window.addEventListener('resize', throttleHandler);
    return () => {
      window.removeEventListener('resize', throttleHandler);
    };
  }, [delay, domElement, handlerRef]);

  return [setElemRef];
}
